import { useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";
import { CalendarIcon, History, Search, X } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import { Card, CardContent } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  SortableTableHead,
  sortItems,
  useTableSort,
} from "~/components/ui/sortable-table-head";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { TablePagination } from "~/components/ui/table-pagination";
import { usePagination } from "~/hooks/usePagination";
import { useKardex } from "~/hooks/wms/useKardex";
import {
  useIngredientMovements,
  useMovementUsers,
} from "~/hooks/wms/useMovements";
import { cn } from "~/lib/utils";
import {
  applyUnifiedClientFilters,
  buildUnifiedRows,
  UNIFIED_KIND_LABELS,
  UNIFIED_LOCATION_OPTIONS,
  type UnifiedKardexFilters,
  type UnifiedKardexItemKind,
  type UnifiedKardexRow,
} from "~/services/wms/unifiedKardex";

const numberFormatter = new Intl.NumberFormat("es-CL");
const PAGE_SIZE = 25;

const KIND_BADGE: Record<UnifiedKardexItemKind, string> = {
  product: "bg-blue-100 text-blue-700",
  raw_material: "bg-orange-100 text-orange-700",
  oil: "bg-amber-100 text-amber-700",
};

const getSortValue = (
  row: UnifiedKardexRow,
  field: string
): string | number | null => {
  switch (field) {
    case "fecha":
      return row.fechaSort;
    case "item":
      return row.item;
    case "movimiento":
      return row.movimiento;
    case "ubicacion":
      return row.ubicacion;
    case "entrada":
      return row.entrada;
    case "salida":
      return row.salida;
    case "saldo":
      return row.saldo;
    case "usuario":
      return row.usuario;
    default:
      return null;
  }
};

interface UnifiedKardexTabProps {
  filters: UnifiedKardexFilters;
  onFiltersChange: (filters: UnifiedKardexFilters) => void;
}

export function UnifiedKardexTab({
  filters,
  onFiltersChange,
}: UnifiedKardexTabProps) {
  const serverFilters = {
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    userId: filters.userId,
    search: filters.search,
  };

  const { data: kardexRows = [], isLoading: kardexLoading } =
    useKardex(serverFilters);
  const { data: ingredientRows = [], isLoading: ingredientLoading } =
    useIngredientMovements(serverFilters);
  const { data: users = [] } = useMovementUsers();

  const isLoading = kardexLoading || ingredientLoading;

  // Each source computes its own per-item running `saldo`, so merging + re-sorting
  // keeps every saldo correct on its row — it only interleaves items in the mixed view.
  const rows = useMemo(
    () =>
      applyUnifiedClientFilters(buildUnifiedRows(kardexRows, ingredientRows), {
        itemKind: filters.itemKind,
        location: filters.location,
      }),
    [kardexRows, ingredientRows, filters.itemKind, filters.location]
  );

  const { sort, toggleSort } = useTableSort({
    field: "fecha",
    direction: "desc",
  });
  const sortedRows = sortItems(rows, sort, getSortValue);
  const { page, setPage, pageCount, pageItems, total } = usePagination(
    sortedRows,
    PAGE_SIZE
  );

  // Debounced free-text search (resolves to product SKU/name + ingredient code/name).
  const [searchValue, setSearchValue] = useState(filters.search || "");
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue !== filters.search) {
        onFiltersChange({ ...filters, search: searchValue || undefined });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue, filters, onFiltersChange]);

  const hasFilters =
    filters.dateFrom ||
    filters.dateTo ||
    filters.itemKind ||
    filters.location ||
    filters.userId ||
    filters.search;

  const clearFilters = () => {
    setSearchValue("");
    onFiltersChange({});
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "h-9 w-full justify-start text-left font-normal sm:w-[150px]",
                !filters.dateFrom && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {filters.dateFrom
                ? dayjs(filters.dateFrom).format("DD/MM/YYYY")
                : "Desde"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              selected={
                filters.dateFrom ? new Date(filters.dateFrom) : undefined
              }
              onSelect={(date) =>
                onFiltersChange({
                  ...filters,
                  dateFrom: date
                    ? dayjs(date).startOf("day").toISOString()
                    : undefined,
                })
              }
              initialFocus
            />
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "h-9 w-full justify-start text-left font-normal sm:w-[150px]",
                !filters.dateTo && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {filters.dateTo
                ? dayjs(filters.dateTo).format("DD/MM/YYYY")
                : "Hasta"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              selected={filters.dateTo ? new Date(filters.dateTo) : undefined}
              onSelect={(date) =>
                onFiltersChange({
                  ...filters,
                  dateTo: date
                    ? dayjs(date).endOf("day").toISOString()
                    : undefined,
                })
              }
              initialFocus
            />
          </PopoverContent>
        </Popover>

        <Select
          value={filters.itemKind || "all"}
          onValueChange={(value) =>
            onFiltersChange({
              ...filters,
              itemKind:
                value === "all"
                  ? undefined
                  : (value as UnifiedKardexItemKind),
            })
          }
        >
          <SelectTrigger className="h-9 w-full sm:w-[160px]">
            <SelectValue placeholder="Tipo de ítem" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los ítems</SelectItem>
            <SelectItem value="product">Producto</SelectItem>
            <SelectItem value="raw_material">Insumo</SelectItem>
            <SelectItem value="oil">Aceite</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={filters.location || "all"}
          onValueChange={(value) =>
            onFiltersChange({
              ...filters,
              location: value === "all" ? undefined : value,
            })
          }
        >
          <SelectTrigger className="h-9 w-full sm:w-[150px]">
            <SelectValue placeholder="Ubicación" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las ubicaciones</SelectItem>
            {UNIFIED_LOCATION_OPTIONS.map((loc) => (
              <SelectItem key={loc} value={loc}>
                {loc}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.userId || "all"}
          onValueChange={(value) =>
            onFiltersChange({
              ...filters,
              userId: value === "all" ? undefined : value,
            })
          }
        >
          <SelectTrigger className="h-9 w-full sm:w-[160px]">
            <SelectValue placeholder="Usuario" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los usuarios</SelectItem>
            {users.map((user) => (
              <SelectItem key={user.id} value={user.id}>
                {user.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative w-full sm:w-[240px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por código o nombre..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="h-9 w-full pl-9"
          />
        </div>

        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="h-9 text-muted-foreground"
          >
            <X className="mr-1 h-4 w-4" />
            Limpiar
          </Button>
        )}
      </div>

      {isLoading ? (
        <TableSkeleton rows={10} columns={11} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={History}
          title="Sin movimientos"
          description="No hay movimientos para los filtros seleccionados"
        />
      ) : (
        <>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableTableHead
                        sortKey="fecha"
                        sort={sort}
                        onSort={toggleSort}
                        className="w-[130px]"
                      >
                        Fecha
                      </SortableTableHead>
                      <SortableTableHead
                        sortKey="item"
                        sort={sort}
                        onSort={toggleSort}
                      >
                        Ítem
                      </SortableTableHead>
                      <SortableTableHead
                        sortKey="movimiento"
                        sort={sort}
                        onSort={toggleSort}
                        className="w-[150px]"
                      >
                        Movimiento
                      </SortableTableHead>
                      <SortableTableHead
                        sortKey="ubicacion"
                        sort={sort}
                        onSort={toggleSort}
                        className="w-[90px]"
                      >
                        Ubicación
                      </SortableTableHead>
                      <SortableTableHead
                        sortKey="entrada"
                        sort={sort}
                        onSort={toggleSort}
                        className="w-[100px] text-right"
                      >
                        Entrada
                      </SortableTableHead>
                      <SortableTableHead
                        sortKey="salida"
                        sort={sort}
                        onSort={toggleSort}
                        className="w-[100px] text-right"
                      >
                        Salida
                      </SortableTableHead>
                      <SortableTableHead
                        sortKey="saldo"
                        sort={sort}
                        onSort={toggleSort}
                        className="w-[110px] text-right"
                      >
                        Saldo
                      </SortableTableHead>
                      <TableHead className="w-[70px]">UM</TableHead>
                      <TableHead className="w-[130px] text-right">
                        Costo
                      </TableHead>
                      <TableHead className="w-[170px]">Referencia</TableHead>
                      <SortableTableHead
                        sortKey="usuario"
                        sort={sort}
                        onSort={toggleSort}
                        className="w-[140px]"
                      >
                        Usuario
                      </SortableTableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pageItems.map((row) => (
                      <TableRow key={row.id} className="hover:bg-muted/30">
                        <TableCell className="text-muted-foreground">
                          {row.fecha}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {row.item ?? "—"}
                            </span>
                            <span className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                              {row.codigo && (
                                <span className="font-mono">{row.codigo}</span>
                              )}
                              <Badge
                                variant="secondary"
                                className={cn(
                                  "px-1.5 py-0 text-[10px]",
                                  KIND_BADGE[row.kind]
                                )}
                              >
                                {UNIFIED_KIND_LABELS[row.kind]}
                              </Badge>
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>{row.movimiento}</TableCell>
                        <TableCell className="font-medium">
                          {row.ubicacion ?? "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.entrada ? (
                            <span className="text-green-700">
                              {numberFormatter.format(row.entrada)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.salida ? (
                            <span className="text-red-700">
                              {numberFormatter.format(row.salida)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono font-semibold">
                          {row.saldo === null ? (
                            <span className="font-normal text-muted-foreground">
                              —
                            </span>
                          ) : (
                            numberFormatter.format(row.saldo)
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {row.um ?? "—"}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {row.costoTotal !== null ? (
                            <div className="flex flex-col items-end">
                              <span>
                                ${numberFormatter.format(row.costoTotal)}
                              </span>
                              {row.costoUnitario !== null && (
                                <span className="text-xs text-muted-foreground">
                                  ${numberFormatter.format(row.costoUnitario)}/
                                  {row.um}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {row.referencia ?? "—"}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {row.usuario ?? "Sistema"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <TablePagination
            page={page}
            pageCount={pageCount}
            totalItems={total}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            itemLabel="movimientos"
          />
        </>
      )}
    </div>
  );
}
